'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useAuth } from '@/lib/stores/auth-store'
import { ActivityIndicator } from '@/components/ui/activity-indicator'
import { handleAuthError } from '@/lib/api-error-handler'

interface AuthGuardProps {
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function AuthGuard({ children, fallback }: AuthGuardProps): React.ReactElement {
  const { isAuthenticated, isLoading, isInitialized, initializeAuth, session, user } = useAuth()
  const router = useRouter()
  const [hasTenant, setHasTenant] = useState<boolean | null>(null)

  // Check for tenant context before doing anything
  useEffect(() => {
    const cookies = document.cookie.split(';')
    const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))
    setHasTenant(!!tenantCookie)
  }, [])

  useEffect(() => {
    // Only initialize auth if we have a tenant and not already initialized
    if (hasTenant && !isInitialized) {
      initializeAuth()
    }
  }, [hasTenant, isInitialized, initializeAuth, session, user, isAuthenticated])

  useEffect(() => {
    // Only redirect if we have a tenant, we're initialized and definitely not authenticated
    if (hasTenant && isInitialized && !isLoading && !isAuthenticated) {
      console.log('[AuthGuard] User not authenticated, triggering logout and redirect')
      // Use handleAuthError to ensure complete cleanup
      handleAuthError({ message: 'Not authenticated' })
    }
  }, [hasTenant, isAuthenticated, isLoading, isInitialized, router])

  // Show loading while checking for tenant
  if (hasTenant === null) {
    return (
      (fallback as React.ReactElement) || <div className="fixed inset-0 flex items-center justify-center bg-background"><ActivityIndicator size={64} /></div>
    )
  }

  // If no tenant, just render children (homepage will handle showing tenant selection)
  if (hasTenant === false) {
    return <>{children}</>
  }

  // Show loading if not initialized or still loading (only when we have a tenant)
  if (!isInitialized || isLoading) {
    return (
      (fallback as React.ReactElement) || <div className="fixed inset-0 flex items-center justify-center bg-background"><ActivityIndicator size={64} /></div>
    )
  }

  if (!isAuthenticated) {
    return <div className="fixed inset-0 flex items-center justify-center bg-background"><ActivityIndicator size={64} /></div>
  }

  return <>{children}</>
}

// Hook for conditional rendering based on auth state
export function useAuthGuard() {
  const { isAuthenticated, isLoading, isInitialized, user, session } = useAuth()

  return {
    isAuthenticated,
    isLoading,
    isInitialized,
    user,
    session,
    canRender: isInitialized && !isLoading && isAuthenticated,
  }
}